import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Share2, Flame, Snowflake, Wind, Clock, Settings, Star, Search, Loader, AlertCircle } from 'lucide-react';

// Using free, no-API-key-required services:
// - Open-Meteo for weather data (https://open-meteo.com)
// - Nominatim for geocoding (OpenStreetMap)
// - No signup or API keys needed!

const HoodieOrNah = () => {
  const [location, setLocation] = useState('');
  const [weatherData, setWeatherData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tolerance, setTolerance] = useState(0);
  const [useCelsius, setUseCelsius] = useState(false);
  const [savedLocations, setSavedLocations] = useState([]);
  const [showSettings, setShowSettings] = useState(false);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [autocompleteResults, setAutocompleteResults] = useState([]);
  const [toast, setToast] = useState(null);
  const autocompleteRef = useRef(null);

  // Load saved preferences from localStorage
  useEffect(() => {
    const savedPrefs = localStorage.getItem('hoodieornah-prefs');
    if (savedPrefs) {
      const prefs = JSON.parse(savedPrefs);
      setTolerance(prefs.tolerance || 0);
      setUseCelsius(prefs.useCelsius || false);
      setSavedLocations(prefs.savedLocations || []);
      setLocation(prefs.lastLocation || '');
    }
  }, []);

  // Save preferences to localStorage
  const savePreferences = (updates) => {
    const current = JSON.parse(localStorage.getItem('hoodieornah-prefs') || '{}');
    const updated = { ...current, ...updates };
    localStorage.setItem('hoodieornah-prefs', JSON.stringify(updated));
  };

  // Toast notification
  const showToast = (message, duration = 3000) => {
    setToast(message);
    setTimeout(() => setToast(null), duration);
  };

  // Temperature conversion
  const toDisplayTemp = (fahrenheit) => {
    if (useCelsius) {
      return Math.round((fahrenheit - 32) * 5/9);
    }
    return Math.round(fahrenheit);
  };

  const tempUnit = useCelsius ? '°C' : '°F';

  // Nominatim geocoding search (OpenStreetMap - free, no API key)
  const handleAutocomplete = async (input) => {
    if (input.length < 3) {
      setShowAutocomplete(false);
      return;
    }

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(input)}&format=json&limit=5&addressdetails=1`,
        {
          headers: {
            'User-Agent': 'HoodieOrNah/1.0' // Required by Nominatim
          }
        }
      );
      
      const data = await response.json();
      
      if (data && data.length > 0) {
        setAutocompleteResults(data);
        setShowAutocomplete(true);
      } else {
        setShowAutocomplete(false);
      }
    } catch (err) {
      console.error('Geocoding error:', err);
      setShowAutocomplete(false);
    }
  };

  // Handle location input change
  useEffect(() => {
    const timer = setTimeout(() => {
      handleAutocomplete(location);
    }, 300);
    return () => clearTimeout(timer);
  }, [location]);

  // Click outside to close autocomplete
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (autocompleteRef.current && !autocompleteRef.current.contains(event.target)) {
        setShowAutocomplete(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'Escape' && showSettings) {
        setShowSettings(false);
      }
    };
    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [showSettings]);

  // Snark library
  const snarkLibrary = {
    absolutelyNot: [
      "Are you insane? It's literally hot outside. Unless you're trying to speedrun heatstroke, leave the hoodie at home.",
      "A hoodie right now would be a cry for help. Or a really misguided fashion statement. Either way, no.",
      "The only thing you need to layer up is your sunscreen. Put the hoodie down and step away slowly.",
      "I admire your commitment to hoodies, but this is not the hill to die on. Literally, because you might overheat.",
      "Listen, I get it. Hoodies are great. But so is not sweating through all your clothes. Make good choices.",
      "If you wear a hoodie in this weather, I'm calling a wellness check. This is an intervention.",
      "The sun is personally offended you're even considering a hoodie right now. Don't disrespect the sun.",
      "No. Nope. Not happening. Your body temperature and I are in complete agreement on this one.",
      "Look, I could sugarcoat this, but it's too hot for that too. Skip the hoodie before you melt.",
      "Wearing a hoodie right now is the meteorological equivalent of bringing a knife to a gunfight. You'll lose."
    ],
    youllLive: [
      "Technically you don't need it, but I'm not your mom. Bring it if commitment to temperature consistency scares you.",
      "You're in that weird zone where you'll regret every choice equally. Hoodie, no hoodie—either way you'll be slightly uncomfortable.",
      "It's warm enough that the hoodie is optional, but we both know you're bringing it anyway because you never trust the weather.",
      "Will you survive without a hoodie? Yes. Will you spend the whole time wondering if you should've brought one? Also yes.",
      "The hoodie is that friend you don't really need at the party but bring anyway for emotional support.",
      "I mean, you can wear it. Will people judge you? Maybe. Will you care? Probably not. Live your truth.",
      "This temperature is the definition of 'fine.' And we all know 'fine' means absolutely nothing.",
      "Your hoodie will spend most of its time tied around your waist looking decorative. But hey, fashion.",
      "Go ahead and bring it. I know you're going to anyway. This isn't my first rodeo with your trust issues.",
      "It's not hoodie weather, but it's not NOT hoodie weather. Quantum hoodie state. Schrödinger's outerwear."
    ],
    probablySmart: [
      "This is that annoying in-between weather. You'll be too hot with it on, too cold without it. Welcome to purgatory.",
      "The universe is testing you with this temperature. There is no right answer, only suffering.",
      "Classic hoodie weather that will betray you every 20 minutes. You'll be putting it on and taking it off all day.",
      "Congratulations, you've unlocked the most indecisive temperature range. The hoodie goes on. The hoodie comes off. Repeat forever.",
      "This is the temperature where you'll carry the hoodie around like an emotional support animal but never quite commit.",
      "You're gonna want it, regret having it, need it again, then hate it. It's complicated. Like most relationships.",
      "The hoodie is both necessary and annoying. Like taxes or Zoom calls. Just accept this is your life now.",
      "Mother Nature is gaslighting you with this temp. Yes, you need a hoodie. No, you won't be happy about it.",
      "This temperature was specifically designed to make you look stupid no matter what you choose. Pick your battle.",
      "Schrodinger's hoodie weather: you simultaneously need it and don't need it until you step outside and observe your goosebumps."
    ],
    definitelyYes: [
      "Yeah, you're gonna want that hoodie. This is textbook hoodie weather. If you don't bring one, that's on you.",
      "This is what hoodies were invented for. Don't overthink it. Just grab the hoodie and go.",
      "See that hoodie hanging on your chair? Yeah, that one. Grab it. I'll wait. This is non-negotiable.",
      "If you skip the hoodie now, you'll spend the next four hours plotting how to acquire one. Save yourself the trouble.",
      "This is prime hoodie territory. The sweet spot. The comfort zone. Don't be a hero, just wear the hoodie.",
      "The answer is yes. It was always yes. It will continue to be yes. Hoodie up and stop questioning your instincts.",
      "Listen to that little voice telling you to grab a hoodie. For once in your life, that voice is 100% correct.",
      "If I had hands, I would personally put a hoodie on you right now. This is THAT obvious.",
      "Not bringing a hoodie right now is the meteorological equivalent of showing up to a potluck empty-handed. Just don't.",
      "Your future self, shivering and full of regret, would like to have a word with you about this hoodie decision."
    ],
    layerUp: [
      "One hoodie? Cute. Maybe grab two. Or a jacket. Or just move somewhere warmer, I don't know your life.",
      "At this temperature, your hoodie needs a hoodie. Layer up like you're preparing for a polar expedition to the grocery store.",
      "This isn't hoodie weather anymore, this is 'question all your life choices that led you to this climate' weather.",
      "Your hoodie is going to need backup. And possibly a union rep. This is asking a lot of one piece of cotton.",
      "I hope that hoodie has friends, because it's going to need emotional support in this cold.",
      "Single hoodie? In THIS economy? In THIS weather? Please. You need layers on layers on layers.",
      "Time to dress like an onion. A very warm, very layered onion that's making better choices than you did moving here.",
      "Your body heat is about to become a precious resource. Hoard it. Layer everything. Trust no one, especially the wind chill.",
      "If you're not wearing at least three layers, you're doing it wrong. This is advanced-level cold we're dealing with.",
      "The hoodie is a good start, but it's just the opening act. The jacket is the headliner. The scarf is the encore."
    ],
    arctic: [
      "At this point a hoodie is adorable optimism. You need actual winter gear. Maybe a parka. Maybe to just stay inside forever.",
      "A hoodie? Really? That's like bringing a spatula to fight a dragon. You need serious insulation, friend.",
      "This temperature is a war crime. The hoodie is a nice gesture, but you need actual armor against this cold.",
      "I don't even want to tell you to go outside. Can you just... not? Is staying inside an option? Please?",
      "This isn't hoodie weather. This is 'full body hibernation suit' weather. This is 'why do I live here' weather.",
      "Your hoodie is filing a formal complaint about working conditions. It did not sign up for this level of cold.",
      "At this temperature, the hoodie is decorative. What you need is a sleeping bag. With a built-in heater. And regrets.",
      "If you go outside in just a hoodie, I'm reporting you to the authorities. This is self-endangerment.",
      "The cold has won. Just accept it. Wear everything you own. Become a human burrito of warmth and survival.",
      "This temperature should be illegal. The hoodie is a nice try, but you're gonna need NASA-grade cold weather gear."
    ]
  };

  const proTips = [
    "Pro tip: You'll need it now, but you can ditch it by 3pm like the optimist you are.",
    "Friendly reminder: Temperature is a social construct, but hypothermia is very real.",
    "The temperature will betray you at least twice today. Plan accordingly.",
    "Statistically speaking, you'll make the wrong hoodie decision at least once today. It's okay, we all do.",
    "This hourly forecast is basically a horoscope but with actual data. Still won't stop you from being wrong.",
    "Weather changes faster than your commitment to checking weather apps. This is the circle of life.",
    "The 3pm temperature is lying to you. It knows what it did.",
    "Bring the hoodie. You know you're going to regret it either way, might as well have options.",
    "This forecast is your therapist telling you that yes, your trust issues with weather are valid.",
    "Temperatures are suggestions. Your body's response to them is a mystery science may never solve.",
    "That 2-degree difference at 6pm? It's going to feel like 20 degrees. Don't ask me why.",
    "Your past self made plans. Your future self will deal with the consequences. The hoodie is caught in the middle.",
    "Every hour is a new adventure in temperature-based regret. Welcome to the emotional roller coaster.",
    "The forecast says one thing. Your goosebumps will say another. Who are you going to believe?",
    "Somewhere between these hourly temps is the exact moment you'll wish you made different choices."
  ];

  const getRandomSnark = (category) => {
    const options = snarkLibrary[category];
    return options[Math.floor(Math.random() * options.length)];
  };

  const getRandomProTip = () => {
    return proTips[Math.floor(Math.random() * proTips.length)];
  };

  const getIntensityLevel = (temp, tolerance) => {
    const adjustedTemp = temp + (tolerance * 5);
    
    if (adjustedTemp >= 80) return {
      level: 'ABSOLUTELY NOT',
      icon: 'fire',
      snark: getRandomSnark('absolutelyNot'),
      color: 'from-orange-400 via-red-400 to-pink-500',
      needsHoodie: false
    };
    if (adjustedTemp >= 70) return {
      level: "YOU'LL LIVE",
      icon: 'cool',
      snark: getRandomSnark('youllLive'),
      color: 'from-amber-400 via-orange-400 to-red-400',
      needsHoodie: false
    };
    if (adjustedTemp >= 60) return {
      level: 'PROBABLY SMART',
      icon: 'thinking',
      snark: getRandomSnark('probablySmart'),
      color: 'from-emerald-400 via-teal-500 to-cyan-600',
      needsHoodie: true
    };
    if (adjustedTemp >= 50) return {
      level: 'DEFINITELY YES',
      icon: 'check',
      snark: getRandomSnark('definitelyYes'),
      color: 'from-cyan-400 via-blue-400 to-blue-500',
      needsHoodie: true
    };
    if (adjustedTemp >= 40) return {
      level: 'LAYER UP, FOOL',
      icon: 'cold',
      snark: getRandomSnark('layerUp'),
      color: 'from-blue-400 via-blue-500 to-indigo-600',
      needsHoodie: true
    };
    return {
      level: 'ARCTIC EXPEDITION',
      icon: 'freeze',
      snark: getRandomSnark('arctic'),
      color: 'from-indigo-400 via-purple-500 to-purple-600',
      needsHoodie: true
    };
  };

  // Fetch weather from Open-Meteo API (free, no API key required)
  const fetchWeather = async (lat, lon, locationName) => {
    try {
      // Open-Meteo API - completely free, no signup needed
      const response = await fetch(
        `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,apparent_temperature,weather_code,wind_speed_10m&hourly=temperature_2m,weather_code&daily=temperature_2m_max,temperature_2m_min&temperature_unit=fahrenheit&wind_speed_unit=mph&timezone=auto`
      );
      
      if (!response.ok) {
        throw new Error('Weather API request failed');
      }

      const data = await response.json();
      
      // Process weather data
      const intensity = getIntensityLevel(data.current.apparent_temperature, tolerance);
      const proTip = getRandomProTip();
      
      // Map WMO weather codes to our icon types
      const mapCondition = (code) => {
        if (code === 0) return 'sunny'; // Clear sky
        if (code >= 1 && code <= 3) return 'partly-cloudy'; // Partly cloudy
        if (code >= 45 && code <= 48) return 'cloudy'; // Fog
        if (code >= 51 && code <= 67) return 'cloudy'; // Rain
        if (code >= 71 && code <= 77) return 'cloudy'; // Snow
        if (code >= 80 && code <= 99) return 'cloudy'; // Showers/storms
        return 'partly-cloudy';
      };

      // Get weather description from WMO code
      const getWeatherDescription = (code) => {
        const descriptions = {
          0: 'Clear sky',
          1: 'Mainly clear',
          2: 'Partly cloudy', 
          3: 'Overcast',
          45: 'Foggy',
          48: 'Depositing rime fog',
          51: 'Light drizzle',
          53: 'Moderate drizzle',
          55: 'Dense drizzle',
          61: 'Slight rain',
          63: 'Moderate rain',
          65: 'Heavy rain',
          71: 'Slight snow',
          73: 'Moderate snow',
          75: 'Heavy snow',
          80: 'Slight rain showers',
          81: 'Moderate rain showers',
          82: 'Violent rain showers',
          95: 'Thunderstorm',
          96: 'Thunderstorm with slight hail',
          99: 'Thunderstorm with heavy hail'
        };
        return descriptions[code] || 'Partly cloudy';
      };

      const weatherResult = {
        location: locationName,
        current: data.current.temperature_2m,
        feelsLike: data.current.apparent_temperature,
        high: data.daily.temperature_2m_max[0],
        low: data.daily.temperature_2m_min[0],
        condition: getWeatherDescription(data.current.weather_code),
        windSpeed: Math.round(data.current.wind_speed_10m),
        hourly: data.hourly.temperature_2m.slice(0, 5).map((temp, idx) => {
          const hour = new Date();
          hour.setHours(hour.getHours() + idx);
          return {
            time: hour.toLocaleTimeString('en-US', { hour: 'numeric' }),
            temp: temp,
            condition: mapCondition(data.hourly.weather_code[idx])
          };
        }),
        ...intensity,
        proTip: proTip
      };

      setWeatherData(weatherResult);
      savePreferences({ lastLocation: locationName });
      setLoading(false);
    } catch (err) {
      console.error('Weather fetch error:', err);
      setError("Couldn't load weather data. Try again?");
      setLoading(false);
      setTimeout(() => setError(null), 5000);
    }
  };

  // Select location from Nominatim autocomplete
  const selectLocation = (place) => {
    const locationName = place.display_name;
    setLocation(locationName);
    setShowAutocomplete(false);
    
    const lat = parseFloat(place.lat);
    const lon = parseFloat(place.lon);
    handleWeatherFetch(lat, lon, locationName);
  };

  // Handle weather fetch
  const handleWeatherFetch = (lat, lon, locationName) => {
    setLoading(true);
    setError(null);
    fetchWeather(lat, lon, locationName);
  };

  // Handle manual location search
  const handleSearch = () => {
    if (!location) return;
    
    // If using Google Places, get the first autocomplete result
    if (autocompleteResults.length > 0) {
      selectLocation(autocompleteResults[0]);
    } else {
      setError("Please select a location from the suggestions");
      setTimeout(() => setError(null), 3000);
    }
  };

  // Geolocation with Nominatim reverse geocoding
  const useMyLocation = () => {
    setLoading(true);
    setError(null);
    
    if (!navigator.geolocation) {
      setError("Geolocation is not supported by your browser");
      setLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lon = position.coords.longitude;
        
        // Reverse geocode to get location name using Nominatim
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json`,
            {
              headers: {
                'User-Agent': 'HoodieOrNah/1.0'
              }
            }
          );
          const data = await response.json();
          
          // Create a friendly location name
          const locationName = data.address.city || data.address.town || data.address.village || 
                              data.address.county || data.address.state || 'Your Location';
          const state = data.address.state || data.address.country;
          const fullLocation = `${locationName}, ${state}`;
          
          setLocation(fullLocation);
          fetchWeather(lat, lon, fullLocation);
        } catch (err) {
          fetchWeather(lat, lon, 'Your Location');
        }
      },
      (err) => {
        setError("Unable to get your location. Please enter it manually.");
        setLoading(false);
        setTimeout(() => setError(null), 5000);
      }
    );
  };

  const handleShare = async () => {
    const shareText = `${weatherData.level} - ${toDisplayTemp(weatherData.current)}${tempUnit} in ${weatherData.location}\n\n"${weatherData.snark}"`;
    const shareUrl = window.location.href;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Hoodie or Nah?',
          text: shareText,
          url: shareUrl
        });
        showToast('Shared successfully! 🎉');
      } catch (err) {
        if (err.name !== 'AbortError') {
          fallbackShare(shareText, shareUrl);
        }
      }
    } else {
      fallbackShare(shareText, shareUrl);
    }
  };

  const fallbackShare = (text, url) => {
    navigator.clipboard.writeText(`${text}\n\nCheck yours at ${url}`);
    showToast('Copied to clipboard! 📋');
  };

  const handleToleranceChange = (value) => {
    setTolerance(value);
    savePreferences({ tolerance: value });
  };

  const addSavedLocation = () => {
    if (location && !savedLocations.includes(location)) {
      const updated = [...savedLocations, location];
      setSavedLocations(updated);
      savePreferences({ savedLocations: updated });
      showToast('Location saved! ⭐');
    }
  };

  const removeSavedLocation = (loc) => {
    const updated = savedLocations.filter(l => l !== loc);
    setSavedLocations(updated);
    savePreferences({ savedLocations: updated });
  };

  // Icon components (keeping the same as before)
  const IntensityIcon = ({ type }) => {
    const icons = {
      'fire': (
        <div className="w-20 h-20 md:w-24 md:h-24 relative animate-pulse-glow">
          <div className="absolute inset-0 bg-gradient-to-t from-red-500 via-orange-500 to-yellow-400 rounded-full blur-xl opacity-60"></div>
          <svg viewBox="0 0 100 100" className="relative w-full h-full">
            <path d="M50 10 C30 30, 20 50, 50 90 C80 50, 70 30, 50 10 Z" 
                  fill="url(#fire-gradient)" className="drop-shadow-lg"/>
            <defs>
              <linearGradient id="fire-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#FCD34D" />
                <stop offset="50%" stopColor="#F59E0B" />
                <stop offset="100%" stopColor="#EF4444" />
              </linearGradient>
            </defs>
          </svg>
        </div>
      ),
      'cool': (
        <div className="w-20 h-20 md:w-24 md:h-24 relative animate-pulse-glow">
          <div className="absolute inset-0 bg-gradient-to-t from-amber-400 to-orange-400 rounded-full blur-xl opacity-50"></div>
          <svg viewBox="0 0 100 100" className="relative w-full h-full">
            <circle cx="50" cy="50" r="35" fill="url(#cool-gradient)" className="drop-shadow-lg"/>
            <circle cx="35" cy="45" r="8" fill="rgba(0,0,0,0.3)"/>
            <circle cx="65" cy="45" r="8" fill="rgba(0,0,0,0.3)"/>
            <path d="M 35 65 Q 50 75 65 65" stroke="rgba(0,0,0,0.3)" strokeWidth="3" fill="none" strokeLinecap="round"/>
            <defs>
              <linearGradient id="cool-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#FBBF24" />
                <stop offset="100%" stopColor="#F59E0B" />
              </linearGradient>
            </defs>
          </svg>
        </div>
      ),
      'thinking': (
        <div className="w-20 h-20 md:w-24 md:h-24 relative animate-pulse-glow">
          <div className="absolute inset-0 bg-gradient-to-t from-teal-400 to-emerald-400 rounded-full blur-xl opacity-50"></div>
          <svg viewBox="0 0 100 100" className="relative w-full h-full">
            <circle cx="50" cy="50" r="35" fill="url(#thinking-gradient)" className="drop-shadow-lg"/>
            <circle cx="35" cy="45" r="6" fill="rgba(0,0,0,0.4)"/>
            <circle cx="65" cy="45" r="6" fill="rgba(0,0,0,0.4)"/>
            <path d="M 35 62 L 65 62" stroke="rgba(0,0,0,0.4)" strokeWidth="3" strokeLinecap="round"/>
            <defs>
              <linearGradient id="thinking-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#34D399" />
                <stop offset="100%" stopColor="#14B8A6" />
              </linearGradient>
            </defs>
          </svg>
        </div>
      ),
      'check': (
        <div className="w-20 h-20 md:w-24 md:h-24 relative animate-pulse-glow">
          <div className="absolute inset-0 bg-gradient-to-t from-cyan-400 to-blue-400 rounded-full blur-xl opacity-50"></div>
          <svg viewBox="0 0 100 100" className="relative w-full h-full">
            <circle cx="50" cy="50" r="35" fill="url(#check-gradient)" className="drop-shadow-lg"/>
            <path d="M 30 50 L 45 65 L 70 35" stroke="white" strokeWidth="6" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
            <defs>
              <linearGradient id="check-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#22D3EE" />
                <stop offset="100%" stopColor="#3B82F6" />
              </linearGradient>
            </defs>
          </svg>
        </div>
      ),
      'cold': (
        <div className="w-20 h-20 md:w-24 md:h-24 relative animate-pulse-glow">
          <div className="absolute inset-0 bg-gradient-to-t from-blue-500 to-indigo-500 rounded-full blur-xl opacity-50"></div>
          <svg viewBox="0 0 100 100" className="relative w-full h-full">
            <circle cx="50" cy="50" r="35" fill="url(#cold-gradient)" className="drop-shadow-lg"/>
            <path d="M 35 48 Q 35 40 42 40 Q 42 35 48 35 Q 48 40 55 40 Q 55 35 62 35 Q 62 40 65 40 Q 65 48 62 48" 
                  fill="rgba(255,255,255,0.3)"/>
            <circle cx="35" cy="45" r="5" fill="rgba(0,0,0,0.4)"/>
            <circle cx="65" cy="45" r="5" fill="rgba(0,0,0,0.4)"/>
            <path d="M 32 65 Q 50 72 68 65" stroke="rgba(0,0,0,0.4)" strokeWidth="3" fill="none" strokeLinecap="round"/>
            <defs>
              <linearGradient id="cold-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#60A5FA" />
                <stop offset="100%" stopColor="#3B82F6" />
              </linearGradient>
            </defs>
          </svg>
        </div>
      ),
      'freeze': (
        <div className="w-20 h-20 md:w-24 md:h-24 relative animate-pulse-glow">
          <div className="absolute inset-0 bg-gradient-to-t from-indigo-500 to-purple-500 rounded-full blur-xl opacity-60"></div>
          <svg viewBox="0 0 100 100" className="relative w-full h-full">
            <g className="drop-shadow-lg">
              <line x1="50" y1="20" x2="50" y2="80" stroke="url(#freeze-gradient)" strokeWidth="4" strokeLinecap="round"/>
              <line x1="20" y1="50" x2="80" y2="50" stroke="url(#freeze-gradient)" strokeWidth="4" strokeLinecap="round"/>
              <line x1="30" y1="30" x2="70" y2="70" stroke="url(#freeze-gradient)" strokeWidth="4" strokeLinecap="round"/>
              <line x1="70" y1="30" x2="30" y2="70" stroke="url(#freeze-gradient)" strokeWidth="4" strokeLinecap="round"/>
              <line x1="50" y1="20" x2="42" y2="28" stroke="url(#freeze-gradient)" strokeWidth="3"/>
              <line x1="50" y1="20" x2="58" y2="28" stroke="url(#freeze-gradient)" strokeWidth="3"/>
              <line x1="50" y1="80" x2="42" y2="72" stroke="url(#freeze-gradient)" strokeWidth="3"/>
              <line x1="50" y1="80" x2="58" y2="72" stroke="url(#freeze-gradient)" strokeWidth="3"/>
              <line x1="20" y1="50" x2="28" y2="42" stroke="url(#freeze-gradient)" strokeWidth="3"/>
              <line x1="20" y1="50" x2="28" y2="58" stroke="url(#freeze-gradient)" strokeWidth="3"/>
              <line x1="80" y1="50" x2="72" y2="42" stroke="url(#freeze-gradient)" strokeWidth="3"/>
              <line x1="80" y1="50" x2="72" y2="58" stroke="url(#freeze-gradient)" strokeWidth="3"/>
            </g>
            <defs>
              <linearGradient id="freeze-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#A78BFA" />
                <stop offset="50%" stopColor="#8B5CF6" />
                <stop offset="100%" stopColor="#6366F1" />
              </linearGradient>
            </defs>
          </svg>
        </div>
      ),
    };
    return icons[type] || icons['check'];
  };

  const WeatherIcon = ({ condition, size = 'md' }) => {
    const sizeClass = size === 'sm' ? 'w-8 h-8' : 'w-12 h-12';
    
    const icons = {
      'sunny': (
        <svg viewBox="0 0 100 100" className={sizeClass}>
          <circle cx="50" cy="50" r="20" fill="#FCD34D"/>
          {[0, 45, 90, 135, 180, 225, 270, 315].map((angle, i) => (
            <line
              key={i}
              x1="50"
              y1="50"
              x2={50 + 35 * Math.cos(angle * Math.PI / 180)}
              y2={50 + 35 * Math.sin(angle * Math.PI / 180)}
              stroke="#FCD34D"
              strokeWidth="3"
              strokeLinecap="round"
            />
          ))}
        </svg>
      ),
      'partly-cloudy': (
        <svg viewBox="0 0 100 100" className={sizeClass}>
          <circle cx="35" cy="35" r="15" fill="#FCD34D"/>
          <ellipse cx="60" cy="60" rx="25" ry="20" fill="white" opacity="0.9"/>
          <ellipse cx="45" cy="65" rx="20" ry="15" fill="white" opacity="0.9"/>
        </svg>
      ),
      'cloudy': (
        <svg viewBox="0 0 100 100" className={sizeClass}>
          <ellipse cx="50" cy="50" rx="30" ry="25" fill="white" opacity="0.9"/>
          <ellipse cx="35" cy="55" rx="25" ry="20" fill="white" opacity="0.9"/>
          <ellipse cx="65" cy="55" rx="25" ry="20" fill="white" opacity="0.9"/>
        </svg>
      ),
      'clear-night': (
        <svg viewBox="0 0 100 100" className={sizeClass}>
          <path d="M 40 20 Q 30 50 40 80 Q 70 70 70 50 Q 70 30 40 20" fill="#E0E7FF"/>
          <circle cx="25" cy="25" r="2" fill="white"/>
          <circle cx="75" cy="30" r="2" fill="white"/>
          <circle cx="70" cy="70" r="2" fill="white"/>
        </svg>
      ),
    };
    return icons[condition] || icons['partly-cloudy'];
  };

  const data = weatherData;

  return (
    <div className={`min-h-screen transition-all duration-1000 ease-in-out ${
      data 
        ? `bg-gradient-to-br ${data.color}` 
        : 'bg-gradient-to-br from-cyan-500 via-teal-600 to-blue-700'
    }`}>
      
      {/* Toast Notification */}
      {toast && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 animate-slide-down">
          <div className="bg-white/95 backdrop-blur-md px-6 py-3 rounded-xl shadow-2xl border border-white/40">
            <p className="text-gray-900 font-medium">{toast}</p>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 animate-slide-down">
          <div className="bg-red-500/95 backdrop-blur-md px-6 py-3 rounded-xl shadow-2xl border border-red-400/40 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-white" />
            <p className="text-white font-medium">{error}</p>
          </div>
        </div>
      )}
      
      {/* Settings Panel */}
      {showSettings && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full space-y-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center">
              <h3 className="text-2xl font-bold text-gray-900">Settings</h3>
              <button onClick={() => setShowSettings(false)} className="text-gray-500 hover:text-gray-700 text-2xl">
                ×
              </button>
            </div>

            <div className="space-y-4">
              {/* Temperature Unit Toggle */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Temperature Unit
                </label>
                <div className="flex items-center gap-3 bg-gray-100 p-1 rounded-xl">
                  <button
                    onClick={() => {
                      setUseCelsius(false);
                      savePreferences({ useCelsius: false });
                    }}
                    className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all ${
                      !useCelsius 
                        ? 'bg-white text-gray-900 shadow-sm' 
                        : 'text-gray-600'
                    }`}
                  >
                    °F
                  </button>
                  <button
                    onClick={() => {
                      setUseCelsius(true);
                      savePreferences({ useCelsius: true });
                    }}
                    className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all ${
                      useCelsius 
                        ? 'bg-white text-gray-900 shadow-sm' 
                        : 'text-gray-600'
                    }`}
                  >
                    °C
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Temperature Tolerance
                </label>
                <div className="flex items-center gap-4">
                  <Snowflake className="w-5 h-5 text-blue-500" />
                  <input
                    type="range"
                    min="-1"
                    max="1"
                    value={tolerance}
                    onChange={(e) => handleToleranceChange(Number(e.target.value))}
                    className="flex-1"
                  />
                  <Flame className="w-5 h-5 text-orange-500" />
                </div>
                <div className="text-center text-sm text-gray-600 mt-2">
                  {tolerance === -1 && "I run cold 🥶"}
                  {tolerance === 0 && "Normal 😐"}
                  {tolerance === 1 && "I run hot 🔥"}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Saved Locations
                </label>
                <div className="space-y-2">
                  {savedLocations.map((loc, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2 bg-gray-100 rounded-lg">
                      <MapPin className="w-4 h-4 text-gray-500" />
                      <span className="flex-1 text-gray-700">{loc}</span>
                      <button 
                        onClick={() => removeSavedLocation(loc)}
                        className="text-red-500 hover:text-red-700"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Container */}
      <div className="min-h-screen flex flex-col items-center justify-center p-4 md:p-8">
        
        {!data ? (
          // Landing State
          <div className="w-full max-w-2xl mx-auto text-center space-y-8 animate-fade-in">
            {/* Settings Button */}
            <div className="absolute top-4 right-4">
              <button
                onClick={() => setShowSettings(true)}
                className="p-3 bg-white/20 backdrop-blur-md rounded-xl text-white hover:bg-white/30 transition-all border border-white/30"
                aria-label="Settings"
              >
                <Settings className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <h1 className="text-6xl md:text-8xl font-black text-white tracking-tight animate-slide-up">
                HOODIE
                <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-300 to-purple-300">
                  OR NAH?
                </span>
              </h1>
              <p className="text-xl md:text-2xl text-white/80 font-light">
                The only weather app with attitude
              </p>
            </div>

            {/* Saved Locations Quick Access */}
            {savedLocations.length > 0 && (
              <div className="flex flex-wrap gap-2 justify-center">
                {savedLocations.map((loc, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      setLocation(loc);
                      // Trigger search for saved location
                      // In production, you'd need to geocode this
                    }}
                    className="px-4 py-2 bg-white/20 backdrop-blur-md rounded-xl text-white hover:bg-white/30 transition-all border border-white/30 flex items-center gap-2"
                  >
                    <Star className="w-4 h-4" />
                    {loc}
                  </button>
                ))}
              </div>
            )}

            <div className="space-y-4">
              <div className="flex gap-2 max-w-md mx-auto relative" ref={autocompleteRef}>
                <div className="flex-1 relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-white/60">
                    <Search className="w-5 h-5" />
                  </div>
                  <input
                    type="text"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="Search for a location..."
                    className="w-full pl-12 pr-6 py-4 rounded-2xl text-lg bg-white/20 backdrop-blur-md border-2 border-white/30 text-white placeholder-white/60 focus:outline-none focus:border-white/60 transition-all"
                    onKeyPress={(e) => e.key === 'Enter' && !loading && handleSearch()}
                    disabled={loading}
                  />
                  
                  {/* Autocomplete Dropdown - Nominatim Results */}
                  {showAutocomplete && autocompleteResults.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-2 bg-white/95 backdrop-blur-md rounded-2xl border border-white/40 overflow-hidden shadow-2xl z-10 animate-slide-down">
                      {autocompleteResults.map((result, idx) => (
                        <button
                          key={idx}
                          onClick={() => selectLocation(result)}
                          className="w-full px-6 py-3 text-left hover:bg-purple-100 transition-colors flex items-center gap-3 border-b border-gray-100 last:border-0"
                        >
                          <MapPin className="w-4 h-4 text-gray-400" />
                          <div>
                            <div className="text-gray-900 font-medium">
                              {result.address.city || result.address.town || result.address.village || result.address.county || result.name}
                            </div>
                            <div className="text-sm text-gray-500">
                              {result.address.state || result.address.country}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  onClick={handleSearch}
                  disabled={loading}
                  className="px-8 py-4 rounded-2xl bg-white text-purple-900 font-bold text-lg hover:bg-white/90 transition-all hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader className="w-5 h-5 animate-spin" />
                      <span className="hidden md:inline">Loading...</span>
                    </>
                  ) : (
                    'GO'
                  )}
                </button>
              </div>
              
              <button
                onClick={useMyLocation}
                disabled={loading}
                className="text-white/70 hover:text-white transition-all flex items-center gap-2 mx-auto group disabled:opacity-50"
              >
                <MapPin className="w-5 h-5 group-hover:rotate-12 transition-transform" />
                Or use my location
              </button>
            </div>

            {/* Footer */}
            <div className="pt-12 space-y-2 text-white/50 text-sm max-w-2xl mx-auto text-center">
              <div className="flex items-center justify-center gap-2 flex-wrap">
                <span>Probably more accurate than your gut feeling</span>
                <span>•</span>
                <span>Weather by Open-Meteo • Location by OpenStreetMap</span>
              </div>
              <div className="text-white/40 text-xs">
                Not liable for your poor hoodie decisions, hypothermia, or trust issues with weather apps.
              </div>
              <div className="pt-2">
                <a href="mailto:info@hoodieornah.com" className="text-white/60 hover:text-white/80 transition-all underline">
                  info@hoodieornah.com
                </a>
                <span className="text-white/40 ml-2">• We'll read it. Eventually. Probably.</span>
              </div>
            </div>
          </div>
        ) : (
          // Results State (keeping all the same UI from before)
          <div className="w-full max-w-5xl mx-auto space-y-6 animate-fade-in">
            
            {/* Top Bar */}
            <div className="flex justify-between items-center">
              <button
                onClick={() => {
                  setWeatherData(null);
                  setError(null);
                }}
                className="text-white/80 hover:text-white transition-all flex items-center gap-2 group"
              >
                <span className="text-2xl group-hover:-translate-x-1 transition-transform">←</span>
                <span className="font-medium">Back</span>
              </button>
              
              <div className="flex gap-2">
                <button
                  onClick={() => setShowSettings(true)}
                  className="p-3 bg-white/20 backdrop-blur-md rounded-xl text-white hover:bg-white/30 transition-all border border-white/30"
                  aria-label="Settings"
                >
                  <Settings className="w-5 h-5" />
                </button>
                <button
                  onClick={handleShare}
                  className="px-4 py-3 bg-white/20 backdrop-blur-md rounded-xl text-white hover:bg-white/30 transition-all border border-white/30 flex items-center gap-2 font-medium"
                >
                  <Share2 className="w-5 h-5" />
                  Share
                </button>
              </div>
            </div>

            {/* Location */}
            <div className="flex items-center justify-center gap-3">
              <h2 className="text-2xl md:text-3xl text-white font-light">
                {data.location}
              </h2>
              <button 
                onClick={addSavedLocation}
                className="p-2 text-white/60 hover:text-white transition-all" 
                aria-label="Save location"
              >
                <Star className={`w-5 h-5 ${savedLocations.includes(data.location) ? 'fill-white' : ''}`} />
              </button>
            </div>

            {/* Main Verdict */}
            <div className="bg-white/10 backdrop-blur-xl rounded-3xl border-2 border-white/20 p-6 md:p-10 text-center space-y-6 animate-slide-up">
              <div className="space-y-3 flex flex-col items-center">
                <IntensityIcon type={data.icon} />
                <div className="text-5xl md:text-7xl font-black text-white leading-none tracking-tight">
                  {data.level}
                </div>
              </div>

              {/* Current Temp & Feels Like */}
              <div className="flex items-center justify-center gap-6 flex-wrap">
                <div>
                  <div className="text-sm text-white/70 mb-1">ACTUAL</div>
                  <div className="text-6xl md:text-7xl font-black text-white">
                    {toDisplayTemp(data.current)}{tempUnit}
                  </div>
                </div>
                <div className="flex items-center gap-2 bg-white/10 px-4 py-3 rounded-xl">
                  <Wind className="w-5 h-5 text-white/70" />
                  <div>
                    <div className="text-xs text-white/70">FEELS LIKE</div>
                    <div className="text-2xl font-bold text-white">{toDisplayTemp(data.feelsLike)}{tempUnit}</div>
                  </div>
                </div>
              </div>

              {/* Snark */}
              <div className="max-w-2xl mx-auto pt-2">
                <p className="text-lg md:text-xl text-white/90 font-light leading-relaxed italic">
                  "{data.snark}"
                </p>
              </div>
            </div>

            {/* Hourly Breakdown */}
            <div className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 p-6">
              <div className="flex items-center gap-2 mb-4">
                <Clock className="w-5 h-5 text-white" />
                <h3 className="text-lg font-semibold text-white">Hourly Forecast</h3>
              </div>
              <div className="overflow-x-auto -mx-2 px-2 md:overflow-visible">
                <div className="flex md:grid md:grid-cols-5 gap-3 min-w-max md:min-w-0">
                  {data.hourly.map((hour, idx) => (
                    <div key={idx} className="bg-white/10 rounded-xl p-3 text-center border border-white/10 min-w-[100px] md:min-w-0">
                      <div className="text-white/70 text-sm mb-1">{hour.time}</div>
                      <div className="flex justify-center mb-1">
                        <WeatherIcon condition={hour.condition} size="sm" />
                      </div>
                      <div className="text-xl font-bold text-white">{toDisplayTemp(hour.temp)}{tempUnit}</div>
                    </div>
                  ))}
                </div>
              </div>
              <p className="text-white/70 text-sm mt-4 italic">
                {data.proTip}
              </p>
            </div>

            {/* Weather Details Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 p-5 text-center">
                <div className="text-white/70 text-sm font-medium mb-1">HIGH</div>
                <div className="text-3xl font-bold text-white">{toDisplayTemp(data.high)}{tempUnit}</div>
              </div>
              <div className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 p-5 text-center">
                <div className="text-white/70 text-sm font-medium mb-1">LOW</div>
                <div className="text-3xl font-bold text-white">{toDisplayTemp(data.low)}{tempUnit}</div>
              </div>
              <div className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 p-5 text-center">
                <div className="text-white/70 text-sm font-medium mb-1">WIND</div>
                <div className="text-3xl font-bold text-white">{data.windSpeed} mph</div>
              </div>
              <div className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 p-5 text-center">
                <div className="text-white/70 text-sm font-medium mb-1">CONDITIONS</div>
                <div className="text-xl font-bold text-white">{data.condition}</div>
              </div>
            </div>

            {/* Donation CTA */}
            <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-6 text-center">
              <p className="text-white/90 mb-3 text-lg">
                This page runs on sarcasm and server costs. One of those is free.
              </p>
              <a 
  href="https://buymeacoffee.com/hoodieornah" 
  target="_blank" 
  rel="noopener noreferrer"
  className="inline-block px-6 py-3 bg-white/20 hover:bg-white/30 text-white font-medium rounded-xl transition-all border border-white/30 hover:scale-105 active:scale-95"
>
  Support my poor life choices ☕
</a>
            </div>

            {/* Footer */}
            <div className="pt-8 space-y-2 text-white/50 text-sm text-center">
              <div className="flex items-center justify-center gap-2 flex-wrap">
                <span>Probably more accurate than your gut feeling</span>
                <span>•</span>
                <span>Weather by Open-Meteo • Location by OpenStreetMap</span>
              </div>
              <div className="text-white/40 text-xs">
                Not liable for your poor hoodie decisions, hypothermia, or trust issues with weather apps.
              </div>
              <div className="pt-2">
                <a href="mailto:info@hoodieornah.com" className="text-white/60 hover:text-white/80 transition-all underline">
                  info@hoodieornah.com
                </a>
                <span className="text-white/40 ml-2">• We'll read it. Eventually. Probably.</span>
              </div>
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in {
          animation: fade-in 0.6s ease-out;
        }
        
        @keyframes slide-down {
          from {
            opacity: 0;
            transform: translate(-50%, -20px);
          }
          to {
            opacity: 1;
            transform: translate(-50%, 0);
          }
        }
        .animate-slide-down {
          animation: slide-down 0.3s ease-out;
        }
        
        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slide-up 0.5s ease-out;
        }
        
        @keyframes pulse-glow {
          0%, 100% {
            filter: brightness(1);
          }
          50% {
            filter: brightness(1.2);
          }
        }
        .animate-pulse-glow {
          animation: pulse-glow 3s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default HoodieOrNah;